import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  Activity, Briefcase, Download, FileText, Loader2, Receipt, ShieldCheck,
} from "lucide-react";
import { getMyClientDashboard } from "@/lib/api/client-dashboard.functions";
import { MATTER_CATEGORIES } from "@/lib/email-presets";
import { PORTALS, portalForCategory, portalLabel } from "@/lib/client-portals";
import { OnboardingChecklistCard } from "@/components/cv/OnboardingChecklistCard";
import { MyEmailAttachments } from "@/components/portal/MyEmailAttachments";
import { NewFilesNotice } from "@/components/portal/NewFilesNotice";

export const Route = createFileRoute("/_authenticated/portal/dashboard")({
  head: () => ({
    meta: [
      { title: "My client dashboard · NDLCS Portal" },
      {
        name: "description",
        content:
          "Your active and previous services, invoices, documents, reports and a full timeline of every interaction with our team.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: ClientDashboard,
});

const rand = (c: number) =>
  `R${(c / 100).toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function categoryLabel(value: string) {
  return MATTER_CATEGORIES.find((c) => c.value === value)?.label ?? value;
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border bg-card p-3">
      <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
        {label}
      </p>
      <p className="font-display text-xl text-navy">{value}</p>
    </div>
  );
}

function ClientDashboard() {
  const fetchDash = useServerFn(getMyClientDashboard);
  const q = useQuery({ queryKey: ["client-dashboard"], queryFn: () => fetchDash() });
  const d = q.data;

  const portalType =
    d?.profile?.portal_type ?? portalForCategory(d?.profile?.service_category ?? null);
  const portal = PORTALS[portalType as keyof typeof PORTALS] ?? PORTALS.legal;

  return (
    <div className="container-narrow space-y-6 py-6">
      <header>
        <p className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-gold">
          <Briefcase className="h-3.5 w-3.5" /> {portalLabel(portalType)}
        </p>
        <h1 className="font-display text-2xl text-navy sm:text-3xl">My dashboard</h1>
        <p className="mt-1 text-sm text-muted-foreground">{portal.blurb}</p>
        <p className="mt-2 rounded-lg border border-border bg-muted/40 p-3 text-sm text-muted-foreground">
          Everything on your account lives on this one page: your details, your files and
          every email we sent you (with attachments), your services, invoices and a full
          timeline. Nothing else is needed to get started.
        </p>
      </header>

      <NewFilesNotice />

      {portalType === "career" && <OnboardingChecklistCard />}

      <MyEmailAttachments />





      {q.isLoading && (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading your dashboard…
        </p>
      )}

      {d && (
        <>
          <section className="rounded-lg border border-border bg-card p-4">
            <h2 className="font-display text-lg text-navy">Your details</h2>
            <dl className="mt-2 grid gap-2 text-sm sm:grid-cols-2">
              <div><dt className="text-muted-foreground">Full name</dt><dd>{d.profile?.full_name || "—"}</dd></div>
              <div><dt className="text-muted-foreground">Email</dt><dd>{d.profile?.email || "—"}</dd></div>
              <div><dt className="text-muted-foreground">Phone</dt><dd>{d.profile?.phone || "—"}</dd></div>
              <div><dt className="text-muted-foreground">Service category</dt><dd>{categoryLabel(d.profile?.service_category ?? "general")}</dd></div>
              <div>
                <dt className="text-muted-foreground">Client since</dt>
                <dd>
                  {d.profile?.client_since || d.profile?.created_at
                    ? new Date(String(d.profile?.client_since ?? d.profile?.created_at)).toLocaleDateString()
                    : "—"}
                </dd>
              </div>
            </dl>
            <p className="mt-3 flex items-start gap-1.5 text-xs text-muted-foreground">
              <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              This portal holds: {portal.contains.join(", ")}. Records are kept securely for five
              years in line with POPIA.
            </p>
          </section>

          <section className="grid gap-3 sm:grid-cols-4">
            <Stat label="Active services" value={String(d.activeCount)} />
            <Stat label="Previous services" value={String(d.previousCount)} />
            <Stat label="Outstanding" value={rand(d.outstandingCents)} />
            <Stat label="Paid to date" value={rand(d.paidCents)} />
          </section>

          <section>
            <h2 className="mb-2 font-display text-lg text-navy">Services &amp; documents</h2>
            {d.matters.length === 0 ? (
              <p className="rounded-lg border border-border bg-card p-4 text-sm text-muted-foreground">
                Nothing here yet. As soon as our team works on something for you it will appear.
              </p>
            ) : (
              <ul className="space-y-3">
                {d.matters.map((m: any) => (
                  <li key={m.id} className="rounded-lg border border-border bg-card p-4">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-[10px] font-semibold uppercase tracking-widest text-gold">
                          {categoryLabel(m.category)}
                          {m.matter_number ? ` · ${m.matter_number}` : ""}
                        </p>
                        <h3 className="font-display text-base text-navy">{m.title}</h3>
                        <p className="text-xs text-muted-foreground">
                          Opened {new Date(m.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] capitalize">
                        {m.status}
                      </span>
                    </div>
                    {m.documents.length > 0 && (
                      <ul className="mt-3 space-y-1 border-t border-border pt-2">
                        {m.documents.map((doc: any) => (
                          <li key={doc.id} className="flex items-center justify-between gap-2 text-sm">
                            <span className="inline-flex min-w-0 items-center gap-1.5">
                              <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                              <span className="truncate">{doc.name}</span>
                            </span>
                            {doc.url ? (
                              <a href={doc.url} target="_blank" rel="noreferrer"
                                className="inline-flex shrink-0 items-center gap-1 text-xs font-semibold text-navy hover:underline">
                                <Download className="h-3 w-3" /> Download
                              </a>
                            ) : (
                              <span className="shrink-0 text-[11px] text-muted-foreground">Sent by email</span>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section>
            <h2 className="mb-2 flex items-center gap-1.5 font-display text-lg text-navy">
              <Receipt className="h-4 w-4" /> Invoices &amp; payments
            </h2>
            {d.invoices.length === 0 ? (
              <p className="rounded-lg border border-border bg-card p-4 text-sm text-muted-foreground">
                No invoices on your account.
              </p>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-border bg-card">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="p-2">Invoice</th><th className="p-2">Amount</th>
                      <th className="p-2">Status</th><th className="p-2">Due</th><th className="p-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {d.invoices.map((i: any) => (
                      <tr key={i.id} className="border-t border-border">
                        <td className="p-2 font-medium">{i.reference ?? "—"}</td>
                        <td className="p-2">{rand(i.total_cents ?? 0)}</td>
                        <td className="p-2 capitalize">{i.status ?? "unpaid"}</td>
                        <td className="p-2">{i.due_date ? new Date(i.due_date).toLocaleDateString() : "—"}</td>
                        <td className="p-2 text-right">
                          {i.public_token && (
                            <a href={`/invoice/${i.public_token}`} target="_blank" rel="noreferrer"
                              className="text-xs font-semibold text-navy hover:underline">
                              View
                            </a>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section>
            <h2 className="mb-2 flex items-center gap-1.5 font-display text-lg text-navy">
              <Activity className="h-4 w-4" /> Timeline of interactions
            </h2>
            {d.timeline.length === 0 ? (
              <p className="rounded-lg border border-border bg-card p-4 text-sm text-muted-foreground">
                Your activity timeline will build up as we work on your matters.
              </p>
            ) : (
              <ol className="space-y-2 border-l-2 border-border pl-4">
                {d.timeline.map((e: any) => (
                  <li key={e.id} className="relative">
                    <span className="absolute -left-[21px] top-1.5 h-2 w-2 rounded-full bg-gold" />
                    <p className="text-sm font-medium text-navy">{e.title}</p>
                    {e.detail && <p className="text-xs text-muted-foreground">{e.detail}</p>}
                    <p className="text-[11px] text-muted-foreground">
                      {new Date(e.created_at).toLocaleString()}
                    </p>
                  </li>
                ))}
              </ol>
            )}
          </section>

          <p className="text-xs text-muted-foreground">
            Looking for a specific matter?{" "}
            <Link to="/portal/matters" className="font-semibold text-navy hover:underline">
              Open my matters
            </Link>
            .
          </p>
        </>
      )}
    </div>
  );
}
